import type { ReactNode } from "react";
import { YStack, XStack, View, Text } from "tamagui";
import { RiAddLine } from "react-icons/ri";
import { colors } from "@styles/colors";

/** 一個帳戶可用的付款工具 */
export interface AccountMethod {
  id: string;
  type: "card" | "line" | "jko" | "monthly";
  name: string;
  owner?: string;
  isDefault?: boolean;
}

export type PayType = AccountMethod["type"];

/** 各付款類型的視覺樣式（chip 配色 + 縮寫） */
export const typeStyleMap: Record<PayType, { bg: string; fg: string; abbr: string; label: string }> = {
  card: { bg: colors.primaryBg, fg: colors.primaryText, abbr: "卡", label: "信用卡" },
  line: { bg: colors.bgGreen, fg: colors.greenText, abbr: "L", label: "LINE Pay" },
  jko: { bg: colors.bgRed, fg: colors.redText, abbr: "街", label: "街口" },
  monthly: { bg: colors.bgPurple, fg: colors.purpleDark, abbr: "月", label: "月結" },
};

/** 付款類型小色塊 */
export function TypeChip({ type }: { type: PayType }) {
  const s = typeStyleMap[type];
  return (
    <View w={36} h={36} br={9} bg={s.bg} ai="center" jc="center" flexShrink={0}>
      <Text fontSize={13} fontWeight="800" color={s.fg}>
        {s.abbr}
      </Text>
    </View>
  );
}

/** 區塊空狀態 */
export function EmptyState({ text }: { text: string }) {
  return (
    <Text fontSize={13} color={colors.hint} ta="center" py="$5">
      {text}
    </Text>
  );
}

interface AccountMethodRowProps {
  method: AccountMethod;
  /** 副標題說明 e.g.「月結帳單 · 由負責人結算」 */
  sub: string;
  isDefault: boolean;
  onSetDefault: () => void;
  isLast: boolean;
  /** 額外內容（例如「我的卡」下方的授權成員 chips） */
  children?: ReactNode;
}

/** 付款工具列：色塊 + 名稱/說明 + 預設標籤或「設為預設」按鈕，可附加 children */
export function AccountMethodRow({ method, sub, isDefault, onSetDefault, isLast, children }: AccountMethodRowProps) {
  return (
    <YStack bbw={isLast ? 0 : 1} bbc={colors.borderLight}>
      <XStack ai="center" gap="$3" px="$3.5" py="$3">
        <TypeChip type={method.type} />
        <YStack f={1} miw={0}>
          <Text fontSize={14} fontWeight="600" color={colors.text} numberOfLines={1}>
            {method.name}
          </Text>
          <Text fontSize={11} color={colors.hint} mt="$0.5">
            {sub}
          </Text>
        </YStack>
        {isDefault ? (
          <View br={10} px="$2" py="$1" bg={colors.primaryBg} flexShrink={0}>
            <Text fontSize={11} fontWeight="700" color={colors.primaryText}>
              預設
            </Text>
          </View>
        ) : (
          <View br={10} px="$2.5" py="$1.5" bw={1} bc={colors.border} cursor="pointer" flexShrink={0} onPress={onSetDefault}>
            <Text fontSize={11} fontWeight="700" color={colors.primary}>
              設為預設
            </Text>
          </View>
        )}
      </XStack>
      {children}
    </YStack>
  );
}

/** 列表底部的「新增卡片」虛線按鈕 */
export function AddCardFooter({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <XStack
      ai="center"
      jc="center"
      gap="$1.5"
      py="$3"
      btw={1}
      btc={colors.border}
      cursor="pointer"
      onPress={onPress}
      style={{ borderTopStyle: "dashed" }}>
      <RiAddLine size={14} color={colors.primary} />
      <Text fontSize={13} fontWeight="600" color={colors.primary}>
        {label}
      </Text>
    </XStack>
  );
}
